import React, { useState } from "react";
import axios from "axios";
import { gql, useMutation } from "@apollo/client";
import { IoMdCloudUpload } from "react-icons/io";
import {
  PAYMENT_METHODS,
  formatRupees,
  getAttachmentLink,
} from "./paymentQueries";

const SETTLE_BILLS = gql`
  mutation settleBills($data: SettleBillsInput!) {
    settleBills(data: $data) {
      status
      message
    }
  }
`;

// en-CA formats as YYYY-MM-DD in the admin's own timezone
const toDateInput = (value) =>
  (value ? new Date(value) : new Date()).toLocaleDateString("en-CA");

const RecordPaymentForm = ({
  bills,
  dealerName,
  initialDate,
  initialMethod,
  onClose,
  onSuccess,
}) => {
  const [date, setDate] = useState(toDateInput(initialDate));
  const [paymentMethod, setPaymentMethod] = useState(initialMethod || "");
  const [reference, setReference] = useState("");
  const [payerName, setPayerName] = useState("");
  const [description, setDescription] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [settleBills, { loading }] = useMutation(SETTLE_BILLS);

  const total =
    bills.reduce(
      (sum, bill) => sum + Math.round(Number(bill.amount) * 100),
      0,
    ) / 100;

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(
        "https://api.barbera.in/img/upload",
        formData,
      );
      if (data.success === true) {
        setAttachmentUrl(data.url);
      }
    } catch (error) {
      console.error(error);
      alert(`${error.message} Try Again`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date || !paymentMethod) {
      alert("Please enter payment date and method");
      return;
    }

    if (!attachmentUrl) {
      alert("Please upload the payment proof");
      return;
    }

    try {
      const res = await settleBills({
        variables: {
          data: {
            paymentIds: bills.map((bill) => bill.id),
            amount: total,
            date: new Date(date).toISOString(),
            paymentMethod,
            reference,
            payerName,
            description,
            attachmentUrl,
          },
        },
      });
      const result = res.data.settleBills;

      if (result.status === "SUCCESS") {
        onClose();
        onSuccess?.();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Record payment failed:", error);
      alert("Unable to record the payment. Please try again.");
    }
  };

  const inputClass =
    "border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800 outline-none";

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col space-y-4 text-gray-800"
    >
      <h3 className="text-xl font-semibold text-[#FBB033] border-b pb-3">
        Record Payment
      </h3>

      <div className="rounded-md border border-gray-200 p-3 text-sm">
        <p className="font-semibold mb-2">{dealerName}</p>
        {bills.map((bill) => (
          <div key={bill.id} className="flex justify-between py-0.5">
            <span className="text-gray-600">
              Bill{" "}
              {bill.billDate
                ? new Date(bill.billDate).toLocaleDateString("en-IN")
                : "(no date)"}
            </span>
            <span>{formatRupees(bill.amount)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t mt-2 pt-2 font-semibold">
          <span>{bills.length} bills</span>
          <span>{formatRupees(total)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">Payment Date</label>
          <input
            type="date"
            required
            min="2020-01-01"
            max={toDateInput()}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">Payment Method</label>
          <select
            required
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className={inputClass}
          >
            <option value="">Select</option>
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">
            UTR / Reference (optional)
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm mb-1 text-gray-600">Payer Name</label>
          <input
            type="text"
            value={payerName}
            onChange={(e) => setPayerName(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex flex-col">
        <label className="text-sm mb-1 text-gray-600">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 px-3 py-2 border rounded-md bg-gray-100 hover:bg-gray-200 cursor-pointer text-sm">
          <IoMdCloudUpload />
          Upload Payment Proof *
          <input type="file" className="hidden" onChange={handleUpload} />
        </label>
        {attachmentUrl && (
          <a
            target="_blank"
            rel="noreferrer"
            href={getAttachmentLink(attachmentUrl)}
            className="text-[#FBB033] text-sm"
          >
            View
          </a>
        )}
        {!attachmentUrl && (
          <span className="text-xs text-gray-500">
            Required: screenshot or receipt of this payment
          </span>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="bg-[#FBB033] px-5 py-2 rounded-md font-semibold text-black disabled:opacity-60"
        >
          Save Payment
        </button>
      </div>
    </form>
  );
};

export default RecordPaymentForm;
